// Система движения DOM-слоя и сцен (ux/12 §8 «Меньше движения», §10; ux/13 §6). Общие длительности и кривые,
// появление, уход, ступенчатое появление списков, пружина, твин и «призрак» ушедшего экрана.
// Правила: анимируется только transform и opacity (без перерисовки раскладки); при prefers-reduced-motion
// или настройке «Меньше движения» (data-motion="reduce" на корне, state/prefs) — только плавное появление,
// без сдвигов, тряски и вспышек. Длинное движение (> DUR.slow) всегда пропускается тапом — у каждого места свой пропуск.
import { useLayoutEffect, type Inputs } from 'preact/hooks';
import type { RefObject } from 'preact';

/** Длительности, мс. Переход экрана ≤ 250 мс, лист 200–240 мс ease-out (ux/12 §10). */
export const DUR = { tap: 90, fast: 160, base: 240, slow: 380, scene: 700 } as const;

/** Кривые для CSS и Web Animations. `spring` — пружина с лёгким перелётом (запасной вариант — `back`). */
export const EASE = {
  out: 'cubic-bezier(.22,1,.36,1)',
  in: 'cubic-bezier(.55,0,1,.45)',
  inOut: 'cubic-bezier(.65,0,.35,1)',
  back: 'cubic-bezier(.34,1.56,.64,1)',
  spring: 'cubic-bezier(.34,1.56,.64,1)',
  linear: 'linear',
};

/** Те же кривые функциями — для твинов на rAF и в PixiJS. */
export const ease = {
  linear: (t: number) => t,
  outCubic: (t: number) => 1 - (1 - t) ** 3,
  inCubic: (t: number) => t ** 3,
  inOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  outExpo: (t: number) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t)),
  outBack: (t: number) => { const c = 1.70158; return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2; },
  outElastic: (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : 2 ** (-10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1),
};

/** Пружина как CSS linear(): перелёт ~8%, успокоение к концу. Где linear() нет (старые WebView) — `back`. */
function springEasing(stiffness = 170, damping = 15): string {
  const pts: string[] = [];
  let x = 0;
  let v = 0;
  const dt = 1 / 60;
  for (let i = 0; i <= 40; i++) {
    pts.push(x.toFixed(3));
    for (let k = 0; k < 2; k++) { const a = stiffness * (1 - x) - damping * v; v += a * dt / 2; x += v * dt / 2; }
  }
  pts[pts.length - 1] = '1';
  return `linear(${pts.join(',')})`;
}
try {
  if (globalThis.CSS?.supports?.('animation-timing-function', 'linear(0, 1)')) EASE.spring = springEasing();
} catch { /* без CSS — остаётся back */ }

export type MotionMode = 'full' | 'reduce';

/** Текущий режим: корень документа (applyPrefs) — настройка игрока и prefers-reduced-motion вместе. */
export function motionMode(): MotionMode {
  const d = globalThis.document?.documentElement;
  if (d?.dataset['motion']) return d.dataset['motion'] === 'reduce' ? 'reduce' : 'full';
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'reduce' : 'full';
}
export const reduced = (): boolean => motionMode() === 'reduce';

/** Системная настройка поменялась на ходу — применить заново (applyPrefs читает и её, и настройку игрока). */
export function watchSystemMotion(apply: () => void): void {
  const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
  mq?.addEventListener?.('change', apply);
}

type Kf = Keyframe[];
interface AnimOpts { duration?: number; delay?: number; easing?: string; fill?: FillMode; iterations?: number }

/** Оставить в кадрах только прозрачность: так «Меньше движения» сохраняет плавное появление без сдвигов. */
function opacityOnly(kf: Kf): Kf | null {
  if (!kf.some((k) => 'opacity' in k)) return null;
  return kf.map((k) => ({ opacity: k.opacity ?? 1, ...(k.offset !== undefined ? { offset: k.offset } : {}) }));
}

/**
 * Web Animations с учётом режима. В «Меньше движения» — только кадры прозрачности не дольше DUR.fast,
 * без прозрачности — ничего (null). Нет WAAPI (тесты, старый WebView) — тоже null, состояние конечное.
 */
export function animate(el: Element | null | undefined, kf: Kf, o: AnimOpts = {}): Animation | null {
  if (!el || typeof (el as HTMLElement).animate !== 'function') return null;
  let frames: Kf | null = kf;
  let duration = o.duration ?? DUR.base;
  if (reduced()) {
    frames = opacityOnly(kf);
    duration = Math.min(duration, DUR.fast);
    if (!frames || (o.iterations ?? 1) !== 1) return null;
  }
  try {
    return (el as HTMLElement).animate(frames, { duration, delay: reduced() ? 0 : o.delay ?? 0, easing: o.easing ?? EASE.out, fill: o.fill ?? 'both', iterations: o.iterations ?? 1 });
  } catch { return null; }
}

/** Завершение анимации промисом; отменённая или отсутствующая — сразу. */
export const finished = (a: Animation | null): Promise<void> => (a ? a.finished.then(() => {}, () => {}) : Promise.resolve());

/** Готовые виды появления. Сдвиги маленькие (8–24 px): движение подсказывает направление, а не «летает». */
export const ENTER = {
  fade: [{ opacity: 0 }, { opacity: 1 }],
  rise: [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
  pop: [{ opacity: 0, transform: 'scale(.86)' }, { opacity: 1, transform: 'none' }],
  up: [{ opacity: 0, transform: 'translateY(100%)' }, { opacity: 1, transform: 'none' }],
  fromRight: [{ opacity: 0, transform: 'translateX(28px)' }, { opacity: 1, transform: 'none' }],
  fromLeft: [{ opacity: 0, transform: 'translateX(-28px)' }, { opacity: 1, transform: 'none' }],
  zoom: [{ opacity: 0, transform: 'scale(1.04)' }, { opacity: 1, transform: 'none' }],
} satisfies Record<string, Kf>;
export type EnterKind = keyof typeof ENTER;

export function enter(el: Element | null | undefined, kind: EnterKind = 'rise', o: AnimOpts = {}): Animation | null {
  return animate(el, ENTER[kind], { duration: DUR.base, easing: kind === 'pop' ? EASE.spring : EASE.out, ...o });
}

export function exit(el: Element | null | undefined, kind: EnterKind = 'fade', o: AnimOpts = {}): Promise<void> {
  return finished(animate(el, [...ENTER[kind]].reverse(), { duration: DUR.fast, easing: EASE.in, ...o }));
}

/** Ступенчатое появление списка: шаг 35 мс, не больше 10 ступеней (дальше — разом, чтобы длинный список не тянулся). */
export function stagger(els: Iterable<Element>, kind: EnterKind = 'rise', o: AnimOpts & { step?: number; max?: number } = {}): void {
  const step = o.step ?? 35;
  const max = o.max ?? 10;
  let i = 0;
  for (const el of els) { enter(el, kind, { ...o, delay: (o.delay ?? 0) + Math.min(i, max) * step }); i++; }
}

/** Короткий «толчок» элемента — счётчик изменился, предмет прилетел. */
export function bump(el: Element | null | undefined, k = 1.14): Animation | null {
  return animate(el, [{ transform: 'scale(1)' }, { transform: `scale(${k})`, offset: 0.35 }, { transform: 'scale(1)' }], { duration: DUR.slow, easing: EASE.out, fill: 'none' });
}

/** Покачивание «нельзя»: ошибка, нехватка. */
export function nudge(el: Element | null | undefined): Animation | null {
  return animate(el, [{ transform: 'none' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(5px)' }, { transform: 'translateX(-3px)' }, { transform: 'none' }], { duration: DUR.slow, easing: EASE.out, fill: 'none' });
}

/**
 * Твин на rAF: onUpdate(доля 0…1 после кривой). В «Меньше движения» — сразу конечное значение.
 * Возвращает отмену; skip() — довести до конца немедленно (пропуск тапом).
 */
export function tween(duration: number, onUpdate: (k: number) => void, o: { ease?: (t: number) => number; delay?: number; onDone?: () => void } = {}): { cancel(): void; skip(): void; done: Promise<void> } {
  let raf = 0;
  let resolve!: () => void;
  const done = new Promise<void>((r) => { resolve = r; });
  let over = false;
  const stop = () => { if (raf) cancelAnimationFrame(raf); };
  const finish = () => { if (over) return; over = true; stop(); onUpdate(1); o.onDone?.(); resolve(); };
  if (reduced() || duration <= 0 || typeof requestAnimationFrame !== 'function') { finish(); return { cancel() {}, skip() {}, done }; }
  const f = o.ease ?? ease.outCubic;
  let t0 = 0;
  const frame = (now: number) => {
    if (!t0) t0 = now + (o.delay ?? 0);
    const t = Math.max(0, Math.min(1, (now - t0) / duration));
    onUpdate(f(t));
    if (t < 1) raf = requestAnimationFrame(frame); else finish();
  };
  raf = requestAnimationFrame(frame);
  return { cancel() { over = true; stop(); resolve(); }, skip: finish, done };
}

/** Пружина с состоянием — для сцен PixiJS и счётчиков: step(dt) тянет value к target. */
export class Spring {
  value: number;
  target: number;
  v = 0;
  constructor(v0: number, public stiffness = 220, public damping = 18) { this.value = v0; this.target = v0; }
  step(dt: number): number {
    const steps = Math.max(1, Math.ceil(dt / (1 / 120)));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) { const a = this.stiffness * (this.target - this.value) - this.damping * this.v; this.v += a * h; this.value += this.v * h; }
    if (Math.abs(this.target - this.value) < 1e-3 && Math.abs(this.v) < 1e-3) { this.value = this.target; this.v = 0; }
    return this.value;
  }
  kick(v: number): void { this.v += v; }
  get resting(): boolean { return this.value === this.target && this.v === 0; }
}

/**
 * «Призрак» уходящего узла: копия без тестовых меток и фокуса доигрывает уход, пока Preact уже показал новое.
 * Узел может быть уже снят Preact — тогда копия встаёт в parent (rect — прежнее место). above — поверх нового.
 * Свои CSS-анимации копии выключены (.m-ghost в styles.css), чтобы появление не проигралось заново.
 */
export function ghost(node: Element | null | undefined, kf: Kf, o: AnimOpts & { above?: boolean; parent?: Element | null; rect?: { top: number; left: number; width: number; height: number } } = {}): HTMLElement | null {
  const parent = node?.parentNode ?? o.parent;
  if (!node || !parent || typeof (node as HTMLElement).animate !== 'function') return null;
  if (reduced() && !opacityOnly(kf)) return null;
  const copy = node.cloneNode(true) as HTMLElement;
  for (const el of [copy, ...copy.querySelectorAll<HTMLElement>('[id],[data-testid],[data-tut],[autofocus]')]) {
    el.removeAttribute('id'); el.removeAttribute('data-testid'); el.removeAttribute('data-tut'); el.removeAttribute('autofocus');
  }
  copy.setAttribute('aria-hidden', 'true');
  copy.setAttribute('inert', '');
  copy.classList.add('m-ghost');
  const r = o.rect;
  Object.assign(copy.style, { pointerEvents: 'none', zIndex: o.above ? '20' : '0', ...(r ? { position: 'absolute', top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px`, margin: '0' } : {}) });
  if (o.above) parent.appendChild(copy);
  else parent.insertBefore(copy, node.parentNode === parent ? node : parent.firstChild);
  const a = animate(copy, kf, { duration: DUR.fast, easing: EASE.in, ...o, fill: 'forwards' });
  const drop = () => copy.remove();
  if (a) a.finished.then(drop, drop); else drop();
  setTimeout(drop, (o.duration ?? DUR.fast) + 400); // страховка, если вкладка ушла в фон
  return copy;
}

/** Появление при монтировании (и при смене deps). */
export function useEnter(ref: RefObject<Element>, kind: EnterKind = 'rise', o: AnimOpts = {}, deps: Inputs = []): void {
  useLayoutEffect(() => { enter(ref.current, kind, o); }, deps);
}

/** Ступенчатое появление детей по селектору — один раз при монтировании и при смене deps. */
export function useStagger(ref: RefObject<Element>, selector: string, kind: EnterKind = 'rise', o: AnimOpts & { step?: number; max?: number } = {}, deps: Inputs = []): void {
  useLayoutEffect(() => {
    const root = ref.current;
    if (root) stagger(root.querySelectorAll(selector), kind, o);
  }, deps);
}
